import {
	createContext,
	use,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { ToastContainer } from "./toast-container";

// Push actions — stable for the provider's lifetime. This is what useToast()
// returns, so consumers can safely put `toast` in a dependency array.
type ToastActions = {
	pushError: (message: string, header?: string, lifetime?: number) => void;
	pushWarning: (message: string, header?: string, lifetime?: number) => void;
	pushSuccess: (message: string, header?: string, lifetime?: number) => void;
	push: (
		message: string,
		type: string,
		header: string,
		lifetime: number,
	) => void;
	remove: (id: any) => void;
};

// The toast list — changes on every add/remove. Only the container reads this,
// and it *should* re-render when toasts change.
type ToastData = {
	data: any[];
	remove: (id: any) => void;
};

const ToastActionsContext = createContext<ToastActions>({} as ToastActions);

export const ToastContext = createContext<ToastData>({} as ToastData);

const DEFAULT_INTERVAL = 5000;

const ToastProvider = ({ children }: any) => {
	const isMounted = useRef(true);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const [data, setData] = useState<any[]>([]);

	const remove = useCallback((id: any) => {
		if (isMounted.current) {
			setData((prevState) => prevState.filter((e) => e.id != id));
		}
	}, []);

	const Push = useCallback(
		(message: string, type: string, header: string, lifetime: number) => {
			const new_item = {
				id: uuidv4(),
				message: message,
				type: type,
				header: header,
				lifetime: lifetime ? lifetime : DEFAULT_INTERVAL,
			};
			if (isMounted.current) {
				setData((prevState) => [...prevState, new_item]);
			}
		},
		[],
	);

	const PushError = useCallback(
		(message: string, header?: string, lifetime?: number) =>
			Push(message, "Error", header || "", lifetime || DEFAULT_INTERVAL),
		[Push],
	);
	const PushWarning = useCallback(
		(message: string, header?: string, lifetime?: number) =>
			Push(message, "Warning", header || "", lifetime || DEFAULT_INTERVAL),
		[Push],
	);
	const PushSuccess = useCallback(
		(message: string, header?: string, lifetime?: number) =>
			Push(message, "Success", header || "", lifetime || DEFAULT_INTERVAL),
		[Push],
	);

	// Stable for the provider's lifetime — every dep is itself stable.
	const actions = useMemo<ToastActions>(
		() => ({
			push: Push,
			pushError: PushError,
			pushWarning: PushWarning,
			pushSuccess: PushSuccess,
			remove,
		}),
		[Push, PushError, PushWarning, PushSuccess, remove],
	);

	// New identity only when the toast list actually changes.
	const dataValue = useMemo<ToastData>(
		() => ({ data, remove }),
		[data, remove],
	);

	return (
		<ToastActionsContext.Provider value={actions}>
			<ToastContext.Provider value={dataValue}>
				<ToastContainer />
				{children}
			</ToastContext.Provider>
		</ToastActionsContext.Provider>
	);
};

const uuidv4 = () => {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0,
			v = c == "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

const useToast = () => use(ToastActionsContext);

export { useToast, ToastProvider };
